import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    IGetEmployeeTimeoffOverview,
    ICancelTimeoffRequest,
    ICreateTimeOffRequest,
    IGetTimeOffBalance,
    IGetEmployeeTimeoffBalanceHistoryList,
    IGetEmployeeTimeoffRequestsList,
    IGetTimeOffRequestList,
    IRejectWithValue,
    IUpdateTimeoffRequest,
    GetManagerEmployees,
    ManagerTimesheetEmployee,
    PhpResponse,
    Timeoff,
    TimeoffBalanceHistory,
    TimeoffBalance,
    TimeoffCategoryListEndpointResponse,
    TimeoffCategoryListResult,
    TimeoffCategoryOverviewEndpointResponse,
    TimeoffCategoryOverviewResponse,
    TimeoffCategoryType,
    TimeoffEarningCodeType,
    TimeoffPolicy,
    TimeoffRequest,
} from "../../../types";
import { phpClient } from "../../../network";
import {
    CreateTimeoffRuleRequestType,
    GetTimeoffPolicyListRequest,
    IGetTimeOffRequestDetails,
    UpdateTimeOffRuleRequest,
    UpdateTimeoffRequestLogPayload,
} from "../../../types/request/timeoff";

const DEFAULT_CATEGORY_PAGE_LIMIT = 20;

function normalizeCategoryOverviewResponse(
    response: TimeoffCategoryOverviewEndpointResponse
): TimeoffCategoryOverviewResponse {
    return {
        category: response.data?.category,
        balance: response.data?.balance,
        summary: response.data?.summary,
    };
}

function normalizeCategoryListResponse<TRecord>(
    response: TimeoffCategoryListEndpointResponse<TRecord>,
    fallbackPage: number = 1,
    fallbackLimit: number = DEFAULT_CATEGORY_PAGE_LIMIT
): TimeoffCategoryListResult<TRecord> {
    const records = response.data?.records ?? [];

    return {
        records,
        meta: response.meta ?? {
            count: records.length,
            page: fallbackPage,
            limit: fallbackLimit,
            has_more: false,
        },
    };
}

export const getTimeoffPolicyList = createAsyncThunk<TimeoffPolicy[], GetTimeoffPolicyListRequest, IRejectWithValue>(
    "timeoff/getTimeoffPolicyList",
    async (data, { getState, rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<TimeoffPolicy[]>>({
                url: "/timeoff/timeoff_policy_list",
                data,
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeoffCategoryList = createAsyncThunk<TimeoffCategoryType[], void, IRejectWithValue>(
    "timeoff/getTimeoffCategoryList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<TimeoffCategoryType[]>({
                url: "/timeoff/list_assigned_category",
            });

            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createTimeOffRequest = createAsyncThunk<void, ICreateTimeOffRequest, IRejectWithValue>(
    "timeoff/createTimeOffRequest",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<void>({
                url: "/timeoff/create_timeoff_request",
                data,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeOffRequestList = createAsyncThunk<Timeoff[], IGetTimeOffRequestList, IRejectWithValue>(
    "timeoff/getTimeOffRequestList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<Timeoff[]>({
                url: "/timeoff/timeoff_request_list",
                data,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getManagerTimeoffEmployees = createAsyncThunk<ManagerTimesheetEmployee[], GetManagerEmployees, IRejectWithValue>(
    "timeoff/manager/employees",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any[]>>({
                url: "/employees",
                data: {
                    extraRequestFields: ["department_detail"],
                    l_i_m_i_t: "500",
                    employee_ids: data.employee_id,
                    DEPARTMENT_CODE: data.department_code,
                    GROUPS: data.group_code,
                    USERSITES: data.site_code,
                    module_name: "manager_timeoff",
                },
            });

            return (response.data ?? []).map((employee) => ({
                emp_detail: {
                    employee_id: employee.employee_id?.toString(),
                    emp_name: employee.full_name,
                    profile_image_url: employee.profile_image_url,
                },
                department: employee.department,
                total_hours: "",
            }));
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getEmployeeTimeoffOverview = createAsyncThunk<
    TimeoffCategoryOverviewResponse,
    IGetEmployeeTimeoffOverview,
    IRejectWithValue
>("timeoff/getEmployeeTimeoffOverview", async (data, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<TimeoffCategoryOverviewEndpointResponse>({
            url: "/timeoff/employee_overview",
            data,
        });

        return normalizeCategoryOverviewResponse(response);
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getEmployeeTimeoffRequestsList = createAsyncThunk<
    TimeoffCategoryListResult<Timeoff>,
    IGetEmployeeTimeoffRequestsList,
    IRejectWithValue
>("timeoff/getEmployeeTimeoffRequestsList", async (data, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<TimeoffCategoryListEndpointResponse<Timeoff>>({
            url: "/timeoff/employee_requests_list",
            data,
        });

        return normalizeCategoryListResponse(response, data.page, data.limit);
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getEmployeeTimeoffBalanceHistoryList = createAsyncThunk<
    TimeoffCategoryListResult<TimeoffBalanceHistory>,
    IGetEmployeeTimeoffBalanceHistoryList,
    IRejectWithValue
>("timeoff/getEmployeeTimeoffBalanceHistoryList", async (data, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<TimeoffCategoryListEndpointResponse<TimeoffBalanceHistory>>({
            url: "/timeoff/employee_balance_history_list",
            data,
        });

        return normalizeCategoryListResponse(response, data.page, data.limit);
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

export const getTimeoffRequestDetails = createAsyncThunk<TimeoffRequest | undefined, IGetTimeOffRequestDetails, IRejectWithValue>(
    "timeoff/getTimeOffRequestDetails",
    async (data, { rejectWithValue }) => {
        try {
            const { time_off_request_id } = data;
            const response = await phpClient.get<PhpResponse<TimeoffRequest | undefined>>({
                url: `/timeoff/timeoff_request_details/${time_off_request_id}`,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getBalanceByCategory = createAsyncThunk<TimeoffBalance[], IGetTimeOffBalance, IRejectWithValue>(
    "timeoff/getBalanceByCategory",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<TimeoffBalance[]>>({
                url: "/timeoff/fetch_timeoff_balance",
                data,
            });

            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const cancelTimeOffRequest = createAsyncThunk<void, ICancelTimeoffRequest, IRejectWithValue>(
    "timeoff/cancelTimeOffRequest",
    async (data, { rejectWithValue }) => {
        const { request_id } = data;
        try {
            const response = await phpClient.get<void>({
                url: `/timeoff/delete_timeoff_request/${request_id}`,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateTimeoffRequest = createAsyncThunk<void, IUpdateTimeoffRequest, IRejectWithValue>(
    "timeoff/updateTimeOffRequest", // Corrected the action name
    async (data, { rejectWithValue }) => {
        const { time_off_request_id, ...request } = data;
        try {
            await phpClient.post<void>({
                url: `/timeoff/update_timeoff_request/${time_off_request_id}`,
                data: request,
            });
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createTimeoffPolicy = createAsyncThunk<PhpResponse<any>, CreateTimeoffRuleRequestType, IRejectWithValue>(
    "timeoff/createTimeoffPolicy",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: "/timeoff/create_timeoff_policy",
                data,
            });
            return response; // Ensure the correct response is returned
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateTimeoffPolicy = createAsyncThunk<PhpResponse<any>, UpdateTimeOffRuleRequest, IRejectWithValue>(
    "timeoff/updateTimeoffPolicy",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: `/timeoff/update_timeoff_policy/${data.policy_code}`, // Ensure policy_code is correctly used
                data,
            });
            return response; // Ensure the correct response is returned
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeoffEarningCodes = createAsyncThunk<TimeoffEarningCodeType[], void, IRejectWithValue>(
    "timeoff/getTimeoffTypes",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<PhpResponse<TimeoffEarningCodeType[]>>({
                url: "/timeoff/timeoff_type",
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

/** Fire-and-forget: updates timeoff request log. Not awaited. */
export async function updateTimeoffRequestLog(payload: UpdateTimeoffRequestLogPayload): Promise<void> {
    try {
        await phpClient.post<PhpResponse<unknown>>({
            url: "/timeoff/update_timeoff_request_log",
            data: payload,
        });
    } catch {
        // Fire and forget - ignore errors
    }
}
